import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, FileText, CalendarDays, CheckCircle, Clock, XCircle, Phone, Mail, Car, Lock, Loader2, Trash2, LayoutGrid, Plus, X, Search, Star, ExternalLink, Truck, Camera, SkipForward, Copy, Upload, ChevronLeft, ChevronRight, Image, ArrowRightCircle, DollarSign, Bell, TrendingUp, ChevronDown, ChevronUp, AlertCircle, Megaphone, Edit2, LogOut, User, Link, Unlink, Activity, Sun, Moon, Calendar, Home } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../utils/api";
import confetti from "canvas-confetti";
import { DashboardCalendar } from "./DashboardCalendar";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { LoginPage } from "./LoginPage";
import { PhotoLightbox } from "./dashboard/PhotoLightbox";
import { AddEntryModal } from "./dashboard/AddEntryModal";
import { ReviewTrackerSection } from "./dashboard/ReviewTrackerSection";
import { JobTrackerAdmin } from "./dashboard/JobTrackerAdmin";
import { CompletedJobsArchive } from "./dashboard/CompletedJobsArchive";
import { NoticeModal } from "./dashboard/NoticeModal";
import { ActivityLogSection } from "./dashboard/ActivityLogSection";
import { ReferralsSection } from "./dashboard/ReferralsSection";
import { BlogManager } from "./dashboard/BlogManager";
import { TodayView } from "./dashboard/TodayView";
import { VoiceButton } from "./dashboard/VoiceButton";
import { BrainDumpButton } from "./dashboard/BrainDumpButton";
import { EndOfDayModal } from "./dashboard/EndOfDayModal";
import { Skeleton } from "./ui/skeleton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `/api`;

// Skeleton loaders
const SkeletonCard = () => (
  <div className="border p-5" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
    <div className="flex items-center gap-4">
      <Skeleton className="w-12 h-12" style={{ backgroundColor: 'var(--dash-border)' }} />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-5 w-3/4" style={{ backgroundColor: 'var(--dash-border)' }} />
        <Skeleton className="h-4 w-1/2" style={{ backgroundColor: 'var(--dash-border)' }} />
      </div>
      <Skeleton className="w-20 h-8" style={{ backgroundColor: 'var(--dash-border)' }} />
    </div>
  </div>
);

const SkeletonJobList = ({ count = 3 }) => (
  <div className="space-y-2">
    {Array.from({ length: count }, (_, i) => <SkeletonCard key={i} />)}
  </div>
);

// Confetti celebration function
const celebrate = () => {
  confetti({
    particleCount: 100,
    spread: 70,
    origin: { y: 0.6 },
    colors: ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6']
  });
};

const STATUS_COLORS = {
  new: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  contacted: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  confirmed: "bg-green-500/20 text-green-400 border-green-500/30",
  completed: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
  cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
  reschedule: "bg-amber-500/20 text-amber-400 border-amber-500/30",
};

const STATUS_OPTIONS = ["new", "contacted", "confirmed", "awaiting_car", "cancelled"];
const APPT_STATUS_OPTIONS = ["pending", "confirmed", "awaiting_car", "converted", "reschedule", "cancelled"];

// Photo Lightbox Component
export default function DashboardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [authenticated, setAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState("today");
  const [activeGroup, setActiveGroup] = useState("today");
  // Focus / executive-function state
  const [focusTasks, setFocusTasks] = useState([]);
  const [needsAttention, setNeedsAttention] = useState(null);
  const [dailyRecap, setDailyRecap] = useState(null);
  const [showEndOfDay, setShowEndOfDay] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState({ total_quotes: 0, new_quotes: 0, total_appointments: 0, pending_appointments: 0, completed_jobs: 0 });
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState("all");
  const [showAddModal, setShowAddModal] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [reviewTracker, setReviewTracker] = useState({ completed_jobs: [], total: 0, reviews_marked: 0 });
  const [jobs, setJobs] = useState({ jobs: [], stages: [] });
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [lightboxPhotos, setLightboxPhotos] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  // Revenue & Notice Board state
  const [revenue, setRevenue] = useState(null);
  const [notices, setNotices] = useState({ notices: [], recent_count: 0 });
  const [revenueOpen, setRevenueOpen] = useState(false);
  const [noticesOpen, setNoticesOpen] = useState(false);
  const [showAddNotice, setShowAddNotice] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  // Google Calendar state
  const [gcalStatus, setGcalStatus] = useState({ connected: false });
  const [gcalLoading, setGcalLoading] = useState(false);
  // Xero state
  const [xeroStatus, setXeroStatus] = useState({ connected: false });
  const [xeroLoading, setXeroLoading] = useState(false);
  // Theme state
  const [theme, setTheme] = useState(() => localStorage.getItem('dashboard-theme') || 'dark');
  // Paid Bookings state
  const [paidBookings, setPaidBookings] = useState([]);
  const [refundingBooking, setRefundingBooking] = useState(null);
  // Labor Report state
  const [laborReport, setLaborReport] = useState(null);
  const [laborWeekOffset, setLaborWeekOffset] = useState(0);
  const [laborLoading, setLaborLoading] = useState(false);
  const [expandedLaborUser, setExpandedLaborUser] = useState(null);
  // Awaiting Car Modal state (for appointments)
  const [awaitingCarModal, setAwaitingCarModal] = useState(null);
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("");
  // Awaiting Car Modal state (for quotes)
  const [quoteAwaitingCarModal, setQuoteAwaitingCarModal] = useState(null);
  // Status sub-filter state
  const [quoteStatusFilter, setQuoteStatusFilter] = useState("unactioned"); // unactioned, contacted, confirmed, awaiting_car, declined, all
  const [apptStatusFilter, setApptStatusFilter] = useState("unactioned"); // unactioned, confirmed, awaiting_car, converted, cancelled, all

  // Check for Google Calendar OAuth callback params
  useEffect(() => {
    if (searchParams.get('gcal_connected') === 'true') {
      toast.success("Google Calendar connected successfully!");
      fetchGcalStatus();
    }
    if (searchParams.get('gcal_error')) {
      toast.error("Failed to connect Google Calendar. Please try again.");
    }
    // Check for Xero callback
    if (searchParams.get('xero') === 'connected') {
      toast.success("Xero connected successfully!");
      fetchXeroStatus();
    }
    if (searchParams.get('xero') === 'error') {
      toast.error("Failed to connect Xero. Please try again.");
    }
  }, [searchParams]);

  const fetchGcalStatus = async () => {
    try {
      const res = await api.get(`${API}/oauth/calendar/status`);
      setGcalStatus(res.data);
    } catch (err) {
      console.error("Failed to fetch Google Calendar status:", err);
    }
  };

  const fetchXeroStatus = async () => {
    try {
      const res = await api.get(`${API}/xero/status`);
      setXeroStatus(res.data);
    } catch (err) {
      console.error("Failed to fetch Xero status:", err);
    }
  };

  const connectXero = async () => {
    setXeroLoading(true);
    try {
      const res = await api.get(`${API}/xero/connect`);
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (err) {
      toast.error("Failed to start Xero connection");
      setXeroLoading(false);
    }
  };

  const disconnectXero = async () => {
    if (!window.confirm("Disconnect Xero? Invoices won't sync until reconnected.")) return;
    try {
      await api.post(`${API}/xero/disconnect`);
      setXeroStatus({ connected: false });
      toast.success("Xero disconnected");
    } catch (err) {
      toast.error("Failed to disconnect Xero");
    }
  };

  // Theme toggle
  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('dashboard-theme', newTheme);
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const connectGoogleCalendar = async () => {
    setGcalLoading(true);
    try {
      // Pass current origin so backend knows where to redirect back
      const origin = window.location.origin;
      const res = await api.get(`${API}/oauth/calendar/login?origin=${encodeURIComponent(origin)}`);
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error("Failed to start Google Calendar connection");
      setGcalLoading(false);
    }
  };

  const disconnectGoogleCalendar = async () => {
    if (!window.confirm("Disconnect Google Calendar? New appointments won't sync until reconnected.")) return;
    setGcalLoading(true);
    try {
      await api.post(`${API}/oauth/calendar/disconnect`);
      setGcalStatus({ connected: false });
      toast.success("Google Calendar disconnected");
    } catch (err) {
      toast.error("Failed to disconnect Google Calendar");
    } finally {
      setGcalLoading(false);
    }
  };

  const syncAllToCalendar = async () => {
    if (!window.confirm("Sync all existing appointments to Google Calendar?")) return;
    setGcalLoading(true);
    try {
      const res = await api.post(`${API}/oauth/calendar/sync-all`);
      toast.success(`Synced ${res.data.synced} appointments to Google Calendar!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sync appointments");
    } finally {
      setGcalLoading(false);
    }
  };

  const handleAuthenticated = () => {
    const user = localStorage.getItem("vpp_user");
    if (user) {
      const parsed = JSON.parse(user);
      const picture = parsed.picture || localStorage.getItem("vpp_picture");
      setCurrentUser({ ...parsed, picture });
    }
    setAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("vpp_token");
    localStorage.removeItem("vpp_user");
    localStorage.removeItem("vpp_picture");
    setAuthenticated(false);
    setCurrentUser(null);
  };

  // Grouped, ADHD-friendly navigation. Each group holds one or more tabs.
  const NAV_GROUPS = [
    { key: "today", label: "Today", icon: Home, tabs: ["today"] },
    { key: "work", label: "Work", icon: Truck, tabs: ["jobs", "labor"] },
    { key: "leads", label: "Leads", icon: FileText, tabs: ["quotes", "appointments", "payments"] },
    { key: "growth", label: "Growth", icon: TrendingUp, tabs: ["referrals", "reviews", "blog"] },
    { key: "calendar", label: "Calendar", icon: Calendar, tabs: ["calendar"] },
    { key: "activity", label: "Activity", icon: Activity, tabs: ["activity"] },
  ];

  const TAB_LABELS = {
    today: "Today", jobs: "Jobs", labor: "Labor", quotes: "Quotes",
    appointments: "Bookings", payments: "Payments", referrals: "Referrals",
    reviews: "Reviews", blog: "Blog", calendar: "Calendar", activity: "Activity",
  };

  const tabCount = (key) => {
    switch (key) {
      case "jobs": return jobs.jobs?.filter(j => j.status === "in_progress").length || 0;
      case "quotes": return stats.new_quotes;
      case "appointments": return stats.pending_appointments;
      case "payments": return paidBookings.filter(b => b.status === "paid").length || 0;
      case "reviews": return reviewTracker.total - reviewTracker.reviews_marked;
      default: return 0;
    }
  };

  const goTo = (tab, group) => {
    setActiveGroup(group);
    setActiveTab(tab);
  };

  const selectGroup = (group) => {
    setActiveGroup(group.key);
    setActiveTab(group.tabs[0]);
  };

  const openLightbox = (photos, index = 0) => {
    setLightboxPhotos(photos);
    setLightboxIndex(index);
  };

  const filterByTime = (items) => {
    if (timeFilter === "all") return items;
    const now = new Date();
    return items.filter((item) => {
      const d = new Date(item.created_at);
      if (timeFilter === "month") {
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      }
      if (timeFilter === "year") {
        return d.getFullYear() === now.getFullYear();
      }
      return true;
    });
  };

  const filterBySearch = (items) => {
    if (!searchQuery.trim()) return items;
    const q = searchQuery.toLowerCase();
    return items.filter((item) =>
      item.name?.toLowerCase().includes(q) ||
      item.vehicle_model?.toLowerCase().includes(q) ||
      item.phone?.includes(q) ||
      item.email?.toLowerCase().includes(q)
    );
  };

  // Filter by status for quotes
  const filterQuotesByStatus = (items) => {
    if (quoteStatusFilter === "all") return items;
    if (quoteStatusFilter === "unactioned") return items.filter(q => q.status === "new");
    if (quoteStatusFilter === "awaiting") return items.filter(q => q.status === "awaiting_car");
    if (quoteStatusFilter === "declined") return items.filter(q => q.status === "cancelled");
    return items.filter(q => q.status === quoteStatusFilter);
  };

  // Filter by status for appointments
  const filterApptsByStatus = (items) => {
    if (apptStatusFilter === "all") return items;
    if (apptStatusFilter === "unactioned") return items.filter(a => a.status === "pending");
    return items.filter(a => a.status === apptStatusFilter);
  };

  const filteredQuotes = useMemo(() => filterQuotesByStatus(filterBySearch(filterByTime(quotes))), [quotes, timeFilter, searchQuery, quoteStatusFilter]);
  const filteredAppointments = useMemo(() => filterApptsByStatus(filterBySearch(filterByTime(appointments))), [appointments, timeFilter, searchQuery, apptStatusFilter]);

  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState("Initializing...");

  const fetchData = async () => {
    setLoading(true);
    setLoadingProgress(0);
    setLoadingMessage("Loading quotes...");
    try {
      setLoadingProgress(10);
      const quotesRes = await api.get(`${API}/quotes`);
      setQuotes(quotesRes.data);
      
      setLoadingMessage("Loading appointments...");
      setLoadingProgress(25);
      const apptsRes = await api.get(`${API}/appointments`);
      setAppointments(apptsRes.data);
      
      setLoadingMessage("Loading stats...");
      setLoadingProgress(40);
      const statsRes = await api.get(`${API}/dashboard/stats`);
      setStats(statsRes.data);
      
      setLoadingMessage("Loading jobs...");
      setLoadingProgress(55);
      const jobsRes = await api.get(`${API}/admin/jobs`);
      setJobs(jobsRes.data);
      
      setLoadingMessage("Finishing up...");
      setLoadingProgress(70);
      
      // Load remaining data in parallel
      const [reviewRes, revenueRes, noticesRes, paidBookingsRes] = await Promise.all([
        api.get(`${API}/dashboard/review-tracker`),
        api.get(`${API}/admin/revenue`),
        api.get(`${API}/admin/notices`),
        api.get(`${API}/admin/paid-bookings`),
      ]);
      
      setReviewTracker(reviewRes.data);
      setRevenue(revenueRes.data);
      setNotices(noticesRes.data);
      setPaidBookings(paidBookingsRes.data.bookings || []);
      setLoadingProgress(100);
      setLoadingMessage("Complete!");
    } catch (err) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // Targeted refresh functions for better performance
  const refreshQuotes = async () => {
    try {
      const [quotesRes, statsRes] = await Promise.all([
        api.get(`${API}/quotes`),
        api.get(`${API}/dashboard/stats`)
      ]);
      setQuotes(quotesRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error("Failed to refresh quotes");
    }
  };

  const refreshAppointments = async () => {
    try {
      const [apptsRes, statsRes] = await Promise.all([
        api.get(`${API}/appointments`),
        api.get(`${API}/dashboard/stats`)
      ]);
      setAppointments(apptsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error("Failed to refresh appointments");
    }
  };

  const refreshJobs = async () => {
    try {
      const [jobsRes, statsRes, revenueRes, reviewRes] = await Promise.all([
        api.get(`${API}/admin/jobs`),
        api.get(`${API}/dashboard/stats`),
        api.get(`${API}/admin/revenue`),
        api.get(`${API}/dashboard/review-tracker`)
      ]);
      setJobs(jobsRes.data);
      setStats(statsRes.data);
      setRevenue(revenueRes.data);
      setReviewTracker(reviewRes.data);
    } catch (err) {
      toast.error("Failed to refresh jobs");
    }
  };

  // Optimized: Update a single job without re-fetching everything
  const updateSingleJob = async (jobId) => {
    try {
      const res = await api.get(`${API}/admin/jobs/${jobId}/full`);
      setJobs(prev => ({
        ...prev,
        jobs: prev.jobs.map(j => j.id === jobId ? res.data : j)
      }));
    } catch (err) {
      // If single job fetch fails, fall back to full refresh
      await refreshJobs();
    }
  };

  // Light refresh - just update the specific job data, not stats/revenue
  const lightRefreshJobs = async () => {
    try {
      const jobsRes = await api.get(`${API}/admin/jobs`);
      setJobs(jobsRes.data);
    } catch (err) {
      toast.error("Failed to refresh jobs");
    }
  };

  // ── Focus / executive-function ──
  const fetchFocus = async () => {
    try {
      const [tasksRes, attnRes, recapRes] = await Promise.all([
        api.get(`${API}/admin/tasks`),
        api.get(`${API}/admin/needs-attention`),
        api.get(`${API}/admin/daily-recap`),
      ]);
      setFocusTasks(tasksRes.data.active || []);
      setNeedsAttention(attnRes.data);
      setDailyRecap(recapRes.data);
    } catch (err) {
      console.error("Failed to load focus data:", err);
    }
  };

  const createTask = async (text, source = "manual") => {
    try {
      await api.post(`${API}/admin/tasks`, { text, source });
      toast.success("Captured");
      const res = await api.get(`${API}/admin/tasks`);
      setFocusTasks(res.data.active || []);
    } catch (err) {
      toast.error("Failed to save task");
    }
  };

  const completeTask = async (id) => {
    setFocusTasks((prev) => prev.filter((t) => t.id !== id));
    try {
      await api.patch(`${API}/admin/tasks/${id}`, { status: "done" });
      toast.success("Done");
    } catch (err) {
      toast.error("Failed to update task");
      fetchFocus();
    }
  };

  const snoozeTask = async (id, dateStr) => {
    setFocusTasks((prev) => prev.filter((t) => t.id !== id));
    try {
      await api.patch(`${API}/admin/tasks/${id}`, { snooze_until: dateStr });
      toast.success("Snoozed — it'll come back");
    } catch (err) {
      toast.error("Failed to snooze");
      fetchFocus();
    }
  };

  const deleteTask = async (id) => {
    setFocusTasks((prev) => prev.filter((t) => t.id !== id));
    try {
      await api.delete(`${API}/admin/tasks/${id}`);
    } catch (err) {
      toast.error("Failed to delete");
      fetchFocus();
    }
  };

  const openEndOfDay = async () => {
    try {
      const res = await api.get(`${API}/admin/daily-recap`);
      setDailyRecap(res.data);
    } catch (err) { /* use existing */ }
    setShowEndOfDay(true);
  };

  const saveCheckin = async ({ answers, tomorrow_tasks }) => {
    try {
      const res = await api.post(`${API}/admin/daily-checkin`, { answers, tomorrow_tasks });
      toast.success(res.data.tasks_created > 0 ? `Saved — ${res.data.tasks_created} task(s) added for tomorrow` : "Day wrapped up");
      setShowEndOfDay(false);
      fetchFocus();
    } catch (err) {
      toast.error("Failed to save check-in");
    }
  };

  const refreshNotices = async () => {
    try {
      const res = await api.get(`${API}/admin/notices`);
      setNotices(res.data);
    } catch (err) {
      toast.error("Failed to refresh notices");
    }
  };

  const refreshReviews = async () => {
    try {
      const res = await api.get(`${API}/dashboard/review-tracker`);
      setReviewTracker(res.data);
    } catch (err) {
      toast.error("Failed to refresh reviews");
    }
  };

  const refreshPaidBookings = async () => {
    try {
      const res = await api.get(`${API}/admin/paid-bookings`);
      setPaidBookings(res.data.bookings || []);
    } catch (err) {
      toast.error("Failed to refresh paid bookings");
    }
  };

  const fetchLaborReport = async (offset = laborWeekOffset) => {
    setLaborLoading(true);
    try {
      const res = await api.get(`${API}/admin/labor-report?week_offset=${offset}`);
      setLaborReport(res.data);
    } catch (err) {
      console.error("Failed to fetch labor report:", err);
    } finally {
      setLaborLoading(false);
    }
  };

  // Fetch labor report when tab is opened or week changes
  useEffect(() => {
    if (activeTab === "labor") {
      fetchLaborReport(laborWeekOffset);
    }
  }, [activeTab, laborWeekOffset]);

  const handleRefundBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to refund this booking? This action cannot be undone.")) return;
    setRefundingBooking(bookingId);
    try {
      await api.post(`${API}/stripe/refund/${bookingId}`);
      toast.success("Refund processed successfully");
      refreshPaidBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process refund");
    } finally {
      setRefundingBooking(null);
    }
  };

  useEffect(() => { 
    if (authenticated) {
      fetchData();
      fetchFocus();
      fetchGcalStatus();
      fetchXeroStatus();
    }
  }, [authenticated]);

  // Notice board functions
  const addNotice = async (notice) => {
    try {
      await api.post(`${API}/admin/notices`, notice);
      toast.success("Notice added");
      refreshNotices();
      setShowAddNotice(false);
    } catch (err) {
      toast.error("Failed to add notice");
    }
  };

  const updateNotice = async (id, notice) => {
    try {
      await api.patch(`${API}/admin/notices/${id}`, notice);
      toast.success("Notice updated");
      refreshNotices();
      setEditingNotice(null);
    } catch (err) {
      toast.error("Failed to update notice");
    }
  };

  const deleteNotice = async (id) => {
    if (!window.confirm("Delete this notice?")) return;
    try {
      await api.delete(`${API}/admin/notices/${id}`);
      toast.success("Notice deleted");
      refreshNotices();
    } catch (err) {
      toast.error("Failed to delete notice");
    }
  };

  // Update job prices
  const updateJobPrices = async (jobId, quotedPrice, finalPrice) => {
    try {
      await api.patch(`${API}/admin/jobs/${jobId}/prices`, {
        quoted_price: quotedPrice ? parseFloat(quotedPrice) : null,
        final_price: finalPrice ? parseFloat(finalPrice) : null
      });
      toast.success("Prices updated");
      refreshJobs();
    } catch (err) {
      toast.error("Failed to update prices");
    }
  };

  if (!authenticated) {
    return <LoginPage onAuthenticated={handleAuthenticated} />;
  }

  const updateQuoteStatus = async (id, status) => {
    try {
      await api.patch(`${API}/quotes/${id}/status`, { status });
      toast.success(`Quote marked as ${status}`);
      refreshQuotes();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const updateApptStatus = async (id, status) => {
    try {
      await api.patch(`${API}/appointments/${id}/status`, { status });
      toast.success(`Appointment marked as ${status}`);
      refreshAppointments();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const deleteQuote = async (id, name) => {
    if (!window.confirm(`Delete quote from ${name}? This cannot be undone.`)) return;
    try {
      await api.delete(`${API}/quotes/${id}`);
      toast.success("Quote deleted");
      refreshQuotes();
    } catch (err) {
      toast.error("Failed to delete quote");
    }
  };

  const deleteAppointment = async (id, name) => {
    if (!window.confirm(`Delete appointment for ${name}? This cannot be undone.`)) return;
    try {
      await api.delete(`${API}/appointments/${id}`);
      toast.success("Appointment deleted");
      refreshAppointments();
    } catch (err) {
      toast.error("Failed to delete appointment");
    }
  };

  const convertQuoteToJob = async (quoteId, customerName, vehicle) => {
    if (!window.confirm(`Convert quote for ${customerName}'s ${vehicle} to a job? This will create a new job with tracking.`)) return;
    try {
      const res = await api.post(`${API}/admin/quotes/${quoteId}/convert-to-job`);
      toast.success(`Job created! Tracking code: ${res.data.tracking_code}`);
      goTo("jobs", "work"); // Switch to jobs tab to show the new job
      // Refresh both quotes and jobs since quote status changed
      await Promise.all([refreshQuotes(), refreshJobs()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to convert quote");
    }
  };

  const convertAppointmentToJob = async (apptId, customerName, vehicle) => {
    if (!window.confirm(`Convert appointment for ${customerName}'s ${vehicle} to a job? This will create a new job with tracking.`)) return;
    try {
      const res = await api.post(`${API}/admin/appointments/${apptId}/convert-to-job`);
      toast.success(`Job created! Tracking code: ${res.data.tracking_code}`);
      goTo("jobs", "work"); // Switch to jobs tab to show the new job
      // Refresh both appointments and jobs
      await Promise.all([refreshAppointments(), refreshJobs()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to convert appointment");
    }
  };

  const setAppointmentAwaitingCar = async () => {
    if (!awaitingCarModal || !expectedDeliveryDate) {
      toast.error("Please select a drop-off date");
      return;
    }
    try {
      await api.put(`${API}/appointments/${awaitingCarModal.id}/awaiting-car`, {
        expected_delivery_date: expectedDeliveryDate
      });
      toast.success(`Drop-off date set for ${expectedDeliveryDate}`);
      setAwaitingCarModal(null);
      setExpectedDeliveryDate("");
      await refreshAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set drop-off date");
    }
  };

  const setQuoteAwaitingCar = async () => {
    if (!quoteAwaitingCarModal || !expectedDeliveryDate) {
      toast.error("Please select a drop-off date");
      return;
    }
    try {
      // Check if this is an update (already has a date) or initial set
      const isUpdate = quoteAwaitingCarModal.status === "awaiting_car" && quoteAwaitingCarModal.expected_delivery_date;
      
      if (isUpdate) {
        // Update existing drop-off date
        await api.put(`${API}/quotes/${quoteAwaitingCarModal.id}/update-dropoff`, {
          expected_delivery_date: expectedDeliveryDate
        });
        toast.success(`Drop-off date updated to ${expectedDeliveryDate} - customer notified`);
      } else {
        // Initial set
        await api.put(`${API}/quotes/${quoteAwaitingCarModal.id}/awaiting-car`, {
          expected_delivery_date: expectedDeliveryDate
        });
        toast.success(`Drop-off date set for ${expectedDeliveryDate}`);
      }
      
      setQuoteAwaitingCarModal(null);
      setExpectedDeliveryDate("");
      await refreshQuotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set drop-off date");
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-20 transition-colors duration-200" style={{ backgroundColor: 'var(--dash-bg)' }} data-testid="dashboard-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-8">
            <button onClick={() => navigate("/")} data-testid="dashboard-back-btn"
              className="flex items-center gap-2 hover:opacity-80 text-sm tracking-[0.15em] uppercase font-body transition-colors bg-transparent border-none cursor-pointer" style={{ color: 'var(--dash-text-dim)' }}>
              <ArrowLeft size={16} /> Back to Site
            </button>
            {currentUser && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2" style={{ color: 'var(--dash-text-muted)' }}>
                  {currentUser.picture ? (
                    <img src={currentUser.picture} alt={currentUser.name || 'User'} data-testid="user-avatar"
                      className="w-7 h-7 rounded-full object-cover border border-[#3b82f6]/40"
                      referrerPolicy="no-referrer"
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <User size={14} />
                  )}
                  <span className="text-sm font-body" data-testid="user-name">{currentUser.name || currentUser.username}</span>
                </div>
                <button onClick={toggleTheme} data-testid="theme-toggle-btn"
                  className="flex items-center gap-2 px-3 py-1.5 text-xs tracking-[0.1em] uppercase font-body border transition-colors bg-transparent cursor-pointer"
                  style={{ borderColor: 'var(--dash-border)', color: 'var(--dash-text-muted)' }}
                  title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                  {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
                </button>
                <button onClick={handleLogout} data-testid="logout-btn"
                  className="flex items-center gap-2 px-3 py-1.5 text-xs tracking-[0.1em] uppercase font-body border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors bg-transparent cursor-pointer">
                  <LogOut size={12} /> Logout
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between">
            <h1 data-testid="dashboard-heading" className="font-heading text-4xl sm:text-5xl uppercase tracking-tight leading-[0.95]" style={{ color: 'var(--dash-text)' }}>
              Dashboard
            </h1>
            <div className="flex gap-2" style={{ display: activeTab === "today" ? "none" : "flex" }}>
              <button onClick={() => setShowAddModal("quote")} data-testid="add-quote-btn"
                className="flex items-center gap-2 px-4 py-2.5 text-xs tracking-[0.1em] uppercase font-body border transition-colors bg-transparent cursor-pointer" style={{ borderColor: 'var(--dash-border)', color: 'var(--dash-text-muted)' }}>
                <Plus size={14} /> Add Quote
              </button>
              <button onClick={() => setShowAddModal("appointment")} data-testid="add-appointment-btn"
                className="flex items-center gap-2 px-4 py-2.5 text-xs tracking-[0.1em] uppercase font-body border border-[#3b82f6]/30 text-[#3b82f6] hover:bg-[#3b82f6]/10 transition-colors bg-transparent cursor-pointer">
                <Plus size={14} /> Add Booking
              </button>
            </div>
          </div>
        </div>

        {/* Stats — hidden on Today (it has its own focused cards) */}
        {activeTab !== "today" && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 md:grid-cols-4 gap-[1px] mb-10" style={{ backgroundColor: 'var(--dash-border)' }}>
          {[
            { label: "Total Quotes", value: stats.total_quotes, icon: FileText },
            { label: "New Quotes", value: stats.new_quotes, icon: CheckCircle, accent: true },
            { label: "Total Bookings", value: stats.total_appointments, icon: CalendarDays },
            { label: "Pending Bookings", value: stats.pending_appointments, icon: Clock, accent: true },
          ].map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.label} data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="p-6 md:p-8" style={{ backgroundColor: 'var(--dash-card)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon size={16} className={stat.accent ? "text-[#3b82f6]" : ""} style={{ color: stat.accent ? undefined : 'var(--dash-text-dim)' }} />
                  <span className="text-xs tracking-[0.15em] uppercase font-body" style={{ color: 'var(--dash-text-dim)' }}>{stat.label}</span>
                </div>
                <span className="font-heading text-3xl md:text-4xl" style={{ color: 'var(--dash-text)' }}>{stat.value}</span>
              </div>
            );
          })}
        </motion.div>
        )}

        {/* Grouped Navigation — fewer, larger targets */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
          {NAV_GROUPS.map((group) => {
            const Icon = group.icon;
            const isActive = activeGroup === group.key;
            const groupCount = group.tabs.reduce((sum, t) => sum + tabCount(t), 0);
            return (
              <button key={group.key} onClick={() => selectGroup(group)}
                data-testid={`nav-group-${group.key}`}
                className="relative flex flex-col items-center justify-center gap-1.5 min-h-[64px] px-2 py-3 font-heading text-xs sm:text-sm tracking-[0.1em] uppercase cursor-pointer border transition-all"
                style={{
                  backgroundColor: isActive ? 'var(--dash-card)' : 'transparent',
                  borderColor: isActive ? 'var(--accent-blue, #3b82f6)' : 'var(--dash-border)',
                  color: isActive ? 'var(--dash-text)' : 'var(--dash-text-dim)',
                }}
              >
                <Icon size={20} className={isActive ? "text-[#3b82f6]" : ""} />
                {group.label}
                {groupCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-mono bg-[#3b82f6] text-black rounded-full">{groupCount}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Sub-tabs — only when the active group has more than one */}
        {(() => {
          const group = NAV_GROUPS.find(g => g.key === activeGroup);
          if (!group || group.tabs.length < 2) return null;
          return (
            <div className="flex flex-wrap gap-2 mb-6">
              {group.tabs.map((t) => {
                const isActive = activeTab === t;
                const c = tabCount(t);
                return (
                  <button key={t} onClick={() => setActiveTab(t)}
                    data-testid={`tab-${t}`}
                    className="flex items-center gap-2 min-h-[44px] px-5 font-body text-sm tracking-[0.08em] uppercase cursor-pointer border transition-colors"
                    style={{
                      backgroundColor: isActive ? 'rgba(59,130,246,0.12)' : 'transparent',
                      borderColor: isActive ? 'rgba(59,130,246,0.4)' : 'var(--dash-border)',
                      color: isActive ? '#3b82f6' : 'var(--dash-text-dim)',
                    }}
                  >
                    {TAB_LABELS[t]}
                    {c > 0 && <span className="px-1.5 py-0.5 text-[10px] bg-[#3b82f6]/20 text-[#3b82f6] border border-[#3b82f6]/30">{c}</span>}
                  </button>
                );
              })}
            </div>
          );
        })()}
        {/* hidden testid anchors so single-tab groups remain testable */}
        <div className="hidden">
          <span data-testid="tab-today" />
          <span data-testid="tab-calendar" />
          <span data-testid="tab-activity" />
        </div>

        {/* Loading with Progress Bar */}
        {loading && (
          <div className="space-y-6">
            {/* Progress Bar */}
            <div className="p-6" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-body" style={{ color: 'var(--dash-text-muted)' }}>{loadingMessage}</span>
                <span className="font-mono text-sm" style={{ color: 'var(--dash-text)' }}>{loadingProgress}%</span>
              </div>
              <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'var(--dash-border)' }}>
                <div 
                  className="h-full rounded-full bg-[#3b82f6] transition-all duration-300"
                  style={{ width: `${loadingProgress}%` }}
                />
              </div>
            </div>
            
            {/* Skeleton Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Array.from({ length: 4 }, (_, i) => (
                <div key={i} className="border p-6" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
                  <Skeleton className="h-8 w-16 mb-2" style={{ backgroundColor: 'var(--dash-border)' }} />
                  <Skeleton className="h-4 w-24" style={{ backgroundColor: 'var(--dash-border)' }} />
                </div>
              ))}
            </div>
            <SkeletonJobList count={4} />
          </div>
        )}

        {/* Today Tab — focused landing screen */}
        {activeTab === "today" && !loading && (
          <TodayView
            currentUser={currentUser}
            jobs={jobs.jobs || []}
            stagesDef={jobs.stages || []}
            quotes={quotes}
            appointments={appointments}
            notices={notices.notices || []}
            needsAttention={needsAttention}
            tasks={focusTasks}
            onNavigate={goTo}
            onAddBooking={() => setShowAddModal("appointment")}
            onAddQuote={() => setShowAddModal("quote")}
            onCreateJob={() => { goTo("jobs", "work"); setShowCreateJob(true); }}
            onCreateTask={createTask}
            onCompleteTask={completeTask}
            onSnoozeTask={snoozeTask}
            onDeleteTask={deleteTask}
            onOpenEndOfDay={openEndOfDay}
          />
        )}


        {/* Calendar Tab */}
        {activeTab === "calendar" && !loading && (
          <div className="space-y-6">
            {/* Revenue & Notice Board Dropdowns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Revenue Dashboard Dropdown */}
              <div className="border" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
                <button
                  onClick={() => setRevenueOpen(!revenueOpen)}
                  className="w-full p-4 flex items-center justify-between cursor-pointer bg-transparent border-none text-left"
                  data-testid="revenue-dropdown-toggle"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#22c55e]/10 flex items-center justify-center">
                      <DollarSign size={20} className="text-[#22c55e]" />
                    </div>
                    <div>
                      <h3 className="font-heading text-sm uppercase tracking-wide" style={{ color: 'var(--dash-text)' }}>Revenue Dashboard</h3>
                      <p className="text-xs font-body" style={{ color: 'var(--dash-text-dim)' }}>Track weekly, monthly & quarterly earnings</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {revenue && revenue.weekly.jobs > 0 && (
                      <span className="px-2 py-0.5 text-xs bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/30">
                        <TrendingUp size={10} className="inline mr-1" />
                        {revenue.weekly.jobs} this week
                      </span>
                    )}
                    {revenueOpen ? <ChevronUp size={18} style={{ color: 'var(--dash-text-dim)' }} /> : <ChevronDown size={18} style={{ color: 'var(--dash-text-dim)' }} />}
                  </div>
                </button>
                
                {revenueOpen && revenue && (
                  <div className="p-4 border-t" style={{ borderColor: 'var(--dash-border)' }}>
                    {/* Revenue Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                      {[
                        { label: "This Week", value: revenue.weekly.revenue, jobs: revenue.weekly.jobs, color: "#3b82f6" },
                        { label: "This Month", value: revenue.monthly.revenue, jobs: revenue.monthly.jobs, color: "#22c55e" },
                        { label: "This Quarter", value: revenue.quarterly.revenue, jobs: revenue.quarterly.jobs, color: "#f59e0b" },
                        { label: "This Year", value: revenue.yearly.revenue, jobs: revenue.yearly.jobs, color: "#a855f7" },
                      ].map((stat) => (
                        <div key={stat.label} className="p-4 text-center" style={{ backgroundColor: 'var(--dash-border)' }}>
                          <p className="text-xs font-body uppercase tracking-wider mb-1" style={{ color: 'var(--dash-text-dim)' }}>{stat.label}</p>
                          <p className="font-heading text-2xl" style={{ color: stat.color }}>
                            ${stat.value.toLocaleString()}
                          </p>
                          <p className="text-xs font-body" style={{ color: 'var(--dash-text-muted)' }}>{stat.jobs} job{stat.jobs !== 1 ? 's' : ''}</p>
                        </div>
                      ))}
                    </div>
                    
                    {/* Revenue Chart */}
                    <div className="p-4" style={{ backgroundColor: 'var(--dash-border)' }}>
                      <p className="text-xs font-body uppercase tracking-wider mb-4" style={{ color: 'var(--dash-text-dim)' }}>Last 6 Months</p>
                      <div style={{ width: '100%', height: 200 }}>
                        <ResponsiveContainer>
                          <BarChart data={revenue.chart_data}>
                            <XAxis 
                              dataKey="label" 
                              tick={{ fill: 'var(--dash-text-dim)', fontSize: 11 }}
                              axisLine={{ stroke: 'var(--dash-border)' }}
                              tickLine={false}
                            />
                            <YAxis 
                              tick={{ fill: 'var(--dash-text-dim)', fontSize: 11 }}
                              axisLine={{ stroke: 'var(--dash-border)' }}
                              tickLine={false}
                              tickFormatter={(value) => `$${value}`}
                            />
                            <Tooltip
                              contentStyle={{ backgroundColor: 'var(--dash-card)', border: '1px solid var(--dash-border)', borderRadius: 0 }}
                              labelStyle={{ color: 'var(--dash-text)', fontFamily: 'monospace' }}
                              formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']}
                            />
                            <Bar dataKey="revenue" fill="#22c55e" radius={[2, 2, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Notice Board Dropdown */}
              <div className="border" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
                <button
                  onClick={() => setNoticesOpen(!noticesOpen)}
                  className="w-full p-4 flex items-center justify-between cursor-pointer bg-transparent border-none text-left"
                  data-testid="notices-dropdown-toggle"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#f59e0b]/10 flex items-center justify-center">
                      <Megaphone size={20} className="text-[#f59e0b]" />
                    </div>
                    <div>
                      <h3 className="font-heading text-sm uppercase tracking-wide" style={{ color: 'var(--dash-text)' }}>Notice Board</h3>
                      <p className="text-xs font-body" style={{ color: 'var(--dash-text-dim)' }}>Monthly announcements & reminders</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {notices.recent_count > 0 && (
                      <span className="px-2 py-0.5 text-xs bg-[#f59e0b]/20 text-[#f59e0b] border border-[#f59e0b]/30 flex items-center gap-1">
                        <Bell size={10} /> {notices.recent_count} new
                      </span>
                    )}
                    {noticesOpen ? <ChevronUp size={18} style={{ color: 'var(--dash-text-dim)' }} /> : <ChevronDown size={18} style={{ color: 'var(--dash-text-dim)' }} />}
                  </div>
                </button>
                
                {noticesOpen && (
                  <div className="p-4 border-t" style={{ borderColor: 'var(--dash-border)' }}>
                    {/* Add Notice Button */}
                    <button
                      onClick={() => setShowAddNotice(true)}
                      className="w-full mb-4 p-3 border border-dashed transition-colors cursor-pointer bg-transparent flex items-center justify-center gap-2 text-sm font-body"
                      style={{ borderColor: 'var(--dash-border)', color: 'var(--dash-text-muted)' }}
                    >
                      <Plus size={16} /> Add Notice
                    </button>
                    
                    {/* Notices List */}
                    {notices.notices.length === 0 ? (
                      <p className="text-center text-sm font-body py-4" style={{ color: 'var(--dash-text-dim)' }}>No notices yet</p>
                    ) : (
                      <div className="space-y-3 max-h-[300px] overflow-y-auto">
                        {notices.notices.map((notice) => (
                          <div key={notice.id} className={`p-3 border-l-4 ${
                            notice.priority === 'high' ? 'border-red-500 bg-red-500/5' :
                            notice.priority === 'low' ? 'border-zinc-600 bg-zinc-500/5' :
                            'border-[#f59e0b] bg-[#f59e0b]/5'
                          }`}>
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <h4 className="font-heading text-sm flex items-center gap-2" style={{ color: 'var(--dash-text)' }}>
                                {notice.priority === 'high' && <AlertCircle size={14} className="text-red-500" />}
                                {notice.title}
                              </h4>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => setEditingNotice(notice)}
                                  className="p-1 transition-colors bg-transparent border-none cursor-pointer"
                                  style={{ color: 'var(--dash-text-dim)' }}
                                >
                                  <Edit2 size={12} />
                                </button>
                                <button
                                  onClick={() => deleteNotice(notice.id)}
                                  className="p-1 text-red-400 hover:text-red-500 transition-colors bg-transparent border-none cursor-pointer"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            </div>
                            <p className="text-xs font-body whitespace-pre-wrap" style={{ color: 'var(--dash-text-muted)' }}>{notice.content}</p>
                            <p className="text-xs font-mono mt-2" style={{ color: 'var(--dash-text-dim)' }}>
                              {new Date(notice.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Google Calendar Connection */}
            <div className="border p-4" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center ${gcalStatus.connected ? 'bg-[#22c55e]/10' : ''}`} style={{ backgroundColor: gcalStatus.connected ? undefined : 'var(--dash-border)' }}>
                    <CalendarDays size={20} className={gcalStatus.connected ? 'text-[#22c55e]' : ''} style={{ color: gcalStatus.connected ? undefined : 'var(--dash-text-dim)' }} />
                  </div>
                  <div>
                    <h3 className="font-heading text-sm uppercase tracking-wide flex items-center gap-2" style={{ color: 'var(--dash-text)' }}>
                      Google Calendar
                      {gcalStatus.connected && (
                        <span className="px-2 py-0.5 text-xs bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/30 font-body normal-case">
                          Connected
                        </span>
                      )}
                    </h3>
                    <p className="text-xs font-body" style={{ color: 'var(--dash-text-dim)' }}>
                      {gcalStatus.connected 
                        ? `Synced with ${gcalStatus.email}` 
                        : 'Sync appointments to your Google Calendar'}
                    </p>
                  </div>
                </div>
                {gcalStatus.connected ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={syncAllToCalendar}
                      disabled={gcalLoading}
                      className="flex items-center gap-2 px-4 py-2 text-xs tracking-[0.1em] uppercase font-body border border-[#22c55e]/30 text-[#22c55e] hover:bg-[#22c55e]/10 transition-colors bg-transparent cursor-pointer disabled:opacity-50"
                      data-testid="gcal-sync-all-btn"
                    >
                      {gcalLoading ? <Loader2 size={14} className="animate-spin" /> : <CalendarDays size={14} />}
                      Sync All
                    </button>
                    <button
                      onClick={disconnectGoogleCalendar}
                      disabled={gcalLoading}
                      className="flex items-center gap-2 px-4 py-2 text-xs tracking-[0.1em] uppercase font-body border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors bg-transparent cursor-pointer disabled:opacity-50"
                      data-testid="gcal-disconnect-btn"
                    >
                      {gcalLoading ? <Loader2 size={14} className="animate-spin" /> : <Unlink size={14} />}
                      Disconnect
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={connectGoogleCalendar}
                    disabled={gcalLoading}
                    className="flex items-center gap-2 px-4 py-2 text-xs tracking-[0.1em] uppercase font-body border border-[#3b82f6]/30 text-[#3b82f6] hover:bg-[#3b82f6]/10 transition-colors bg-transparent cursor-pointer disabled:opacity-50"
                    data-testid="gcal-connect-btn"
                  >
                    {gcalLoading ? <Loader2 size={14} className="animate-spin" /> : <Link size={14} />}
                    Connect
                  </button>
                )}
              </div>
            </div>

            {/* Xero Accounting Connection */}
            <div className="border p-4" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center ${xeroStatus.connected ? 'bg-[#13B5EA]/10' : ''}`} style={{ backgroundColor: xeroStatus.connected ? undefined : 'var(--dash-border)' }}>
                    <DollarSign size={20} className={xeroStatus.connected ? 'text-[#13B5EA]' : ''} style={{ color: xeroStatus.connected ? undefined : 'var(--dash-text-dim)' }} />
                  </div>
                  <div>
                    <h3 className="font-heading text-sm uppercase tracking-wide flex items-center gap-2" style={{ color: 'var(--dash-text)' }}>
                      Xero Accounting
                      {xeroStatus.connected && (
                        <span className="px-2 py-0.5 text-xs bg-[#13B5EA]/20 text-[#13B5EA] border border-[#13B5EA]/30 font-body normal-case">
                          Connected
                        </span>
                      )}
                    </h3>
                    <p className="text-xs font-body" style={{ color: 'var(--dash-text-dim)' }}>
                      {xeroStatus.connected 
                        ? `Connected to ${xeroStatus.tenant_name}` 
                        : 'Auto-create invoices in Xero'}
                    </p>
                  </div>
                </div>
                {xeroStatus.connected ? (
                  <button
                    onClick={disconnectXero}
                    disabled={xeroLoading}
                    className="flex items-center gap-2 px-4 py-2 text-xs tracking-[0.1em] uppercase font-body border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors bg-transparent cursor-pointer disabled:opacity-50"
                    data-testid="xero-disconnect-btn"
                  >
                    {xeroLoading ? <Loader2 size={14} className="animate-spin" /> : <Unlink size={14} />}
                    Disconnect
                  </button>
                ) : (
                  <button
                    onClick={connectXero}
                    disabled={xeroLoading}
                    className="flex items-center gap-2 px-4 py-2 text-xs tracking-[0.1em] uppercase font-body border border-[#13B5EA]/30 text-[#13B5EA] hover:bg-[#13B5EA]/10 transition-colors bg-transparent cursor-pointer disabled:opacity-50"
                    data-testid="xero-connect-btn"
                  >
                    {xeroLoading ? <Loader2 size={14} className="animate-spin" /> : <Link size={14} />}
                    Connect Xero
                  </button>
                )}
              </div>
            </div>

            {/* Add/Edit Notice Modal */}
            {(showAddNotice || editingNotice) && (
              <NoticeModal
                notice={editingNotice}
                onSave={(data) => editingNotice ? updateNotice(editingNotice.id, data) : addNotice(data)}
                onClose={() => { setShowAddNotice(false); setEditingNotice(null); }}
              />
            )}

            {/* Calendar Component */}
            <DashboardCalendar 
              appointments={appointments} 
              gcalConnected={gcalStatus.connected} 
              dropoffs={quotes.filter(q => q.status === 'awaiting_car' && q.expected_delivery_date)}
            />
          </div>
        )}

        {/* Jobs Tab */}
        {activeTab === "jobs" && !loading && (
          <JobTrackerAdmin jobs={jobs} onUpdate={lightRefreshJobs} onUpdateSingle={updateSingleJob} onFullRefresh={refreshJobs} showCreateJob={showCreateJob} setShowCreateJob={setShowCreateJob} />
        )}

        {/* Search & Time Filter - only for quotes/appointments tabs */}
        {(activeTab === "quotes" || activeTab === "appointments") && (
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            {/* Search Input */}
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--dash-text-dim)' }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, vehicle, phone, email..."
                data-testid="dashboard-search-input"
                className="w-full pl-11 pr-16 py-2.5 border font-body text-sm outline-none focus:border-[#3b82f6]/50 transition-colors"
                style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)', color: 'var(--dash-text)' }}
              />
              <VoiceButton
                onText={(t) => setSearchQuery((prev) => (prev ? prev + " " : "") + t.trim())}
                size={16}
                testId="dashboard-search-voice-btn"
                title="Search by voice"
                className="absolute right-3 top-1/2 -translate-y-1/2"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-9 top-1/2 -translate-y-1/2 transition-colors bg-transparent border-none cursor-pointer"
                  style={{ color: 'var(--dash-text-dim)' }}
                  data-testid="search-clear-btn"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            {/* Time Filter */}
            <div className="flex gap-2" data-testid="time-filter">
          {[
            { key: "all", label: "All" },
            { key: "month", label: "This Month" },
            { key: "year", label: "This Year" },
          ].map((f) => (
            <button key={f.key} onClick={() => setTimeFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={`px-4 py-2 text-xs tracking-[0.1em] uppercase font-body border cursor-pointer transition-colors ${
                timeFilter === f.key
                  ? "bg-[#3b82f6]/20 text-[#3b82f6] border-[#3b82f6]/30"
                  : "bg-transparent"
              }`}
              style={timeFilter !== f.key ? { borderColor: 'var(--dash-border)', color: 'var(--dash-text-dim)' } : undefined}>
              {f.label}
            </button>
          ))}
            </div>
          </div>
        )}

        {/* Status Sub-Filter - for quotes tab */}
        {activeTab === "quotes" && (
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { key: "unactioned", label: "Unactioned", count: quotes.filter(q => q.status === "new").length },
              { key: "contacted", label: "Contacted", count: quotes.filter(q => q.status === "contacted").length },
              { key: "confirmed", label: "Confirmed", count: quotes.filter(q => q.status === "confirmed").length },
              { key: "awaiting", label: "Awaiting Car", count: quotes.filter(q => q.status === "awaiting_car").length },
              { key: "declined", label: "Declined", count: quotes.filter(q => q.status === "cancelled").length },
              { key: "all", label: "All", count: quotes.length },
            ].map((f) => (
              <button key={f.key} onClick={() => setQuoteStatusFilter(f.key)}
                className={`px-3 py-1.5 text-xs tracking-[0.05em] uppercase font-body border cursor-pointer transition-colors ${
                  quoteStatusFilter === f.key
                    ? "bg-[#f59e0b]/20 text-[#f59e0b] border-[#f59e0b]/30"
                    : "bg-transparent"
                }`}
                style={quoteStatusFilter !== f.key ? { borderColor: 'var(--dash-border)', color: 'var(--dash-text-dim)' } : undefined}>
                {f.label} {f.count > 0 && <span className="ml-1 opacity-60">({f.count})</span>}
              </button>
            ))}
          </div>
        )}

        {/* Status Sub-Filter - for appointments tab */}
        {activeTab === "appointments" && (
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { key: "unactioned", label: "Unactioned", count: appointments.filter(a => a.status === "pending").length },
              { key: "confirmed", label: "Confirmed", count: appointments.filter(a => a.status === "confirmed").length },
              { key: "awaiting_car", label: "Awaiting Car", count: appointments.filter(a => a.status === "awaiting_car").length },
              { key: "converted", label: "Converted", count: appointments.filter(a => a.status === "converted").length },
              { key: "cancelled", label: "Cancelled", count: appointments.filter(a => a.status === "cancelled").length },
              { key: "all", label: "All", count: appointments.length },
            ].map((f) => (
              <button key={f.key} onClick={() => setApptStatusFilter(f.key)}
                className={`px-3 py-1.5 text-xs tracking-[0.05em] uppercase font-body border cursor-pointer transition-colors ${
                  apptStatusFilter === f.key
                    ? "bg-[#f59e0b]/20 text-[#f59e0b] border-[#f59e0b]/30"
                    : "bg-transparent"
                }`}
                style={apptStatusFilter !== f.key ? { borderColor: 'var(--dash-border)', color: 'var(--dash-text-dim)' } : undefined}>
                {f.label} {f.count > 0 && <span className="ml-1 opacity-60">({f.count})</span>}
              </button>
            ))}
          </div>
        )}

        {/* Content - Quotes/Appointments only */}
        {(activeTab === "quotes" || activeTab === "appointments") && !loading && (
          activeTab === "quotes" ? (
          <div className="space-y-[1px]" style={{ backgroundColor: 'var(--dash-border)' }} data-testid="quotes-list">
            {filteredQuotes.length === 0 ? (
              <div className="p-12 text-center font-body" style={{ backgroundColor: 'var(--dash-card)', color: 'var(--dash-text-dim)' }}>No quotes found</div>
            ) : filteredQuotes.map((q) => (
              <div key={q.id} data-testid={`quote-item-${q.id}`} className="p-6 md:p-8" style={{ backgroundColor: 'var(--dash-card)' }}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-heading text-xl uppercase tracking-wide" style={{ color: 'var(--dash-text)' }}>{q.name}</h3>
                      <span className={`px-2 py-0.5 text-xs uppercase font-mono border ${STATUS_COLORS[q.status] || STATUS_COLORS.new}`}>
                        {q.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm font-mono" style={{ color: 'var(--dash-text-muted)' }}>
                      <span className="flex items-center gap-1"><Phone size={12} /> {q.phone}</span>
                      {q.email && <span className="flex items-center gap-1"><Mail size={12} /> {q.email}</span>}
                      <span className="flex items-center gap-1"><Car size={12} /> {q.vehicle_model}</span>
                    </div>
                    {/* Show drop-off date prominently if set */}
                    {q.status === "awaiting_car" && q.expected_delivery_date && (
                      <div className="mt-2 flex items-center gap-2 text-sm">
                        <span className="px-2 py-1 bg-[#3b82f6]/20 text-[#3b82f6] border border-[#3b82f6]/30 flex items-center gap-1.5">
                          <Calendar size={12} /> Drop-off: <strong>{q.expected_delivery_date}</strong>
                        </span>
                      </div>
                    )}
                    {q.status === "awaiting_car" && !q.expected_delivery_date && (
                      <div className="mt-2 flex items-center gap-2 text-sm">
                        <span className="px-2 py-1 bg-[#f59e0b]/20 text-[#f59e0b] border border-[#f59e0b]/30 flex items-center gap-1.5 animate-pulse">
                          <Calendar size={12} /> No drop-off date set!
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {/* Awaiting Delivery button - for quotes not yet awaiting car */}
                    {q.status !== "awaiting_car" && q.status !== "cancelled" && !q.converted_to_job && (
                      <button onClick={() => setQuoteAwaitingCarModal(q)}
                        data-testid={`quote-awaiting-${q.id}`}
                        className="px-3 py-1 text-xs tracking-[0.05em] uppercase font-body border border-[#3b82f6]/30 text-[#3b82f6] hover:bg-[#3b82f6]/10 transition-colors cursor-pointer bg-transparent flex items-center gap-1">
                        <Truck size={11} /> Awaiting Delivery
                      </button>
                    )}
                    {/* Show button for awaiting_car status - to SET date if not set, or EDIT if already set */}
                    {q.status === "awaiting_car" && (
                      <button 
                        onClick={() => setQuoteAwaitingCarModal(q)}
                        className={`px-3 py-1 text-xs tracking-[0.05em] uppercase font-body border transition-colors cursor-pointer bg-transparent flex items-center gap-1 ${
                          q.expected_delivery_date 
                            ? 'border-[#3b82f6]/30 text-[#3b82f6] hover:bg-[#3b82f6]/10' 
                            : 'border-[#f59e0b]/50 text-[#f59e0b] hover:bg-[#f59e0b]/10 animate-pulse'
                        }`}
                        title={q.expected_delivery_date ? "Click to change drop-off date" : "Set drop-off date"}
                        data-testid={`quote-edit-dropoff-${q.id}`}
                      >
                        <Truck size={11} /> 
                        {q.expected_delivery_date 
                          ? <>Drop-off: {q.expected_delivery_date} <Edit2 size={10} /></>
                          : <>Set Drop-off Date</>
                        }
                      </button>
                    )}
                    {/* Convert to Job button - only show for confirmed/contacted/awaiting_car quotes */}
                    {(q.status === "confirmed" || q.status === "contacted" || q.status === "new" || q.status === "awaiting_car") && !q.converted_to_job && (
                      <button onClick={() => convertQuoteToJob(q.id, q.name, q.vehicle_model)}
                        data-testid={`quote-convert-${q.id}`}
                        className="px-3 py-1 text-xs tracking-[0.05em] uppercase font-body border border-[#22c55e]/30 text-[#22c55e] hover:bg-[#22c55e]/10 transition-colors cursor-pointer bg-transparent flex items-center gap-1">
                        <ArrowRightCircle size={11} /> Convert to Job
                      </button>
                    )}
                    {q.converted_to_job && (
                      <span className="px-3 py-1 text-xs tracking-[0.05em] uppercase font-body border border-[#22c55e]/20 text-[#22c55e]/60 flex items-center gap-1">
                        <CheckCircle size={11} /> Converted
                      </span>
                    )}
                    {STATUS_OPTIONS.filter(s => s !== q.status && s !== "awaiting_car").map((s) => (
                      <button key={s} onClick={() => updateQuoteStatus(q.id, s)}
                        data-testid={`quote-status-${q.id}-${s}`}
                        className="px-3 py-1 text-xs tracking-[0.05em] uppercase font-body border transition-colors cursor-pointer bg-transparent"
                        style={{ borderColor: 'var(--dash-border)', color: 'var(--dash-text-muted)' }}>
                        {s}
                      </button>
                    ))}
                    <button onClick={() => deleteQuote(q.id, q.name)}
                      data-testid={`quote-delete-${q.id}`}
                      className="px-3 py-1 text-xs tracking-[0.05em] uppercase font-body border border-red-500/20 text-red-500/50 hover:text-red-400 hover:border-red-500/40 transition-colors cursor-pointer bg-transparent flex items-center gap-1">
                      <Trash2 size={11} /> Delete
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-6 text-sm">
                  <div>
                    <span className="font-body" style={{ color: 'var(--dash-text-dim)' }}>Service: </span>
                    <span className="font-body" style={{ color: 'var(--dash-text-muted)' }}>{q.service_type}</span>
                  </div>
                  {q.description && (
                    <div>
                      <span className="font-body" style={{ color: 'var(--dash-text-dim)' }}>Notes: </span>
                      <span className="font-body" style={{ color: 'var(--dash-text-muted)' }}>{q.description}</span>
                    </div>
                  )}
                  <div className="ml-auto">
                    <span className="font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>{new Date(q.created_at).toLocaleString("en-AU")}</span>
                  </div>
                </div>
                {q.photos && q.photos.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs uppercase tracking-wider mb-2 flex items-center gap-2" style={{ color: 'var(--dash-text-dim)' }}>
                      <Image size={12} /> Customer Photos ({q.photos.length})
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      {q.photos.map((p, pi) => (
                        <button
                          key={pi}
                          onClick={() => openLightbox(q.photos, pi)}
                          className="block w-24 h-24 border hover:border-[#3b82f6]/50 transition-colors cursor-pointer bg-transparent p-0 overflow-hidden"
                          style={{ borderColor: 'var(--dash-border)' }}
                        >
                          <img src={p} alt={`Photo ${pi + 1}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {q.voice_note && (
                  <div className="mt-4 flex items-center gap-3 p-3 bg-[#f59e0b]/10 border border-[#f59e0b]/20">
                    <span className="text-xs text-[#f59e0b] uppercase tracking-wider flex items-center gap-2">
                      <Bell size={12} /> Voice Note
                    </span>
                    <audio controls src={q.voice_note} className="h-8 flex-1" style={{ maxWidth: '300px' }}>
                      Your browser does not support audio playback.
                    </audio>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-[1px]" style={{ backgroundColor: 'var(--dash-border)' }} data-testid="appointments-list">
            {filteredAppointments.length === 0 ? (
              <div className="p-12 text-center font-body" style={{ backgroundColor: 'var(--dash-card)', color: 'var(--dash-text-dim)' }}>No appointments found</div>
            ) : filteredAppointments.map((a) => (
              <div key={a.id} data-testid={`appt-item-${a.id}`} className="p-6 md:p-8" style={{ backgroundColor: 'var(--dash-card)' }}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-heading text-xl uppercase tracking-wide" style={{ color: 'var(--dash-text)' }}>{a.name}</h3>
                      <span className={`px-2 py-0.5 text-xs uppercase font-mono border ${STATUS_COLORS[a.status] || STATUS_COLORS.pending}`}>
                        {a.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm font-mono" style={{ color: 'var(--dash-text-muted)' }}>
                      <span className="flex items-center gap-1"><Phone size={12} /> {a.phone}</span>
                      {a.email && <span className="flex items-center gap-1"><Mail size={12} /> {a.email}</span>}
                      <span className="flex items-center gap-1"><Car size={12} /> {a.vehicle_model}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {/* Convert to Job button - show for awaiting_car appointments */}
                    {a.status === "awaiting_car" && !a.converted_to_job && (
                      <button onClick={() => convertAppointmentToJob(a.id, a.name, a.vehicle_model)}
                        data-testid={`appt-convert-${a.id}`}
                        className="px-3 py-1 text-xs tracking-[0.05em] uppercase font-body border border-[#22c55e]/30 text-[#22c55e] hover:bg-[#22c55e]/10 transition-colors cursor-pointer bg-transparent flex items-center gap-1">
                        <ArrowRightCircle size={11} /> Convert to Job
                      </button>
                    )}
                    {a.converted_to_job && (
                      <span className="px-3 py-1 text-xs tracking-[0.05em] uppercase font-body border border-[#22c55e]/20 text-[#22c55e]/60 flex items-center gap-1">
                        <CheckCircle size={11} /> Converted
                      </span>
                    )}
                    {/* Set Delivery Date button - show for pending/confirmed */}
                    {(a.status === "pending" || a.status === "confirmed") && (
                      <button onClick={() => setAwaitingCarModal(a)}
                        data-testid={`appt-set-delivery-${a.id}`}
                        className="px-3 py-1 text-xs tracking-[0.05em] uppercase font-body border border-[#3b82f6]/30 text-[#3b82f6] hover:bg-[#3b82f6]/10 transition-colors cursor-pointer bg-transparent flex items-center gap-1">
                        <CalendarDays size={11} /> Set Drop-off Date
                      </button>
                    )}
                    {APPT_STATUS_OPTIONS.filter(s => s !== a.status && s !== "awaiting_car" && s !== "converted").map((s) => (
                      <button key={s} onClick={() => updateApptStatus(a.id, s)}
                        data-testid={`appt-status-${a.id}-${s}`}
                        className="px-3 py-1 text-xs tracking-[0.05em] uppercase font-body border transition-colors cursor-pointer bg-transparent"
                        style={{ borderColor: 'var(--dash-border)', color: 'var(--dash-text-muted)' }}>
                        {s === "awaiting_car" ? "awaiting car" : s}
                      </button>
                    ))}
                    <button onClick={() => deleteAppointment(a.id, a.name)}
                      data-testid={`appt-delete-${a.id}`}
                      className="px-3 py-1 text-xs tracking-[0.05em] uppercase font-body border border-red-500/20 text-red-500/50 hover:text-red-400 hover:border-red-500/40 transition-colors cursor-pointer bg-transparent flex items-center gap-1">
                      <Trash2 size={11} /> Delete
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <CalendarDays size={14} className="text-[#3b82f6]" />
                    <span className="font-mono" style={{ color: 'var(--dash-text)' }}>{a.preferred_date}</span>
                    <span style={{ color: 'var(--dash-text-dim)' }}>at</span>
                    <span className="font-mono" style={{ color: 'var(--dash-text)' }}>{a.preferred_time}</span>
                  </div>
                  {a.expected_delivery_date && (
                    <div className="flex items-center gap-2">
                      <Truck size={14} className="text-[#22c55e]" />
                      <span style={{ color: 'var(--dash-text-dim)' }}>Drop-off:</span>
                      <span className="font-mono text-[#22c55e]">{a.expected_delivery_date}</span>
                    </div>
                  )}
                  <div>
                    <span className="font-body" style={{ color: 'var(--dash-text-dim)' }}>Service: </span>
                    <span className="font-body" style={{ color: 'var(--dash-text-muted)' }}>{a.service_type}</span>
                  </div>
                  {a.description && (
                    <div>
                      <span className="font-body" style={{ color: 'var(--dash-text-dim)' }}>Notes: </span>
                      <span className="font-body" style={{ color: 'var(--dash-text-muted)' }}>{a.description}</span>
                    </div>
                  )}
                  <div className="ml-auto">
                    <span className="font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>{new Date(a.created_at).toLocaleString("en-AU")}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}

        {/* Reviews Tab */}
        {activeTab === "reviews" && !loading && (
          <ReviewTrackerSection reviewTracker={reviewTracker} onUpdate={refreshReviews} />
        )}

        {/* Referrals Tab */}
        {activeTab === "referrals" && !loading && (
          <ReferralsSection />
        )}

        {/* Activity Log Tab */}
        {activeTab === "activity" && !loading && (
          <ActivityLogSection />
        )}

        {/* Blog Tab */}
        {activeTab === "blog" && !loading && (
          <BlogManager />
        )}

        {/* Payments Tab */}
        {activeTab === "payments" && !loading && (
          <div className="space-y-4" data-testid="payments-tab">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-lg uppercase tracking-wide" style={{ color: 'var(--dash-text)' }}>Paid Bookings</h3>
              <button
                onClick={refreshPaidBookings}
                data-testid="refresh-paid-bookings-btn"
                className="px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors cursor-pointer"
                style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)', color: 'var(--dash-text-muted)' }}
              >
                Refresh
              </button>
            </div>

            {paidBookings.length === 0 ? (
              <div className="border p-12 text-center" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
                <DollarSign size={32} className="mx-auto mb-4" style={{ color: 'var(--dash-text-dim)' }} />
                <p className="font-body" style={{ color: 'var(--dash-text-dim)' }}>No paid bookings yet</p>
                <p className="text-sm font-body mt-2" style={{ color: 'var(--dash-text-dim)' }}>Bookings from Mobile Inspection and Headlight services will appear here</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {paidBookings.map((booking) => {
                  const isRefundable = booking.status === "paid" && new Date(booking.refund_deadline) > new Date();
                  const isInspection = booking.type === "mobile_inspection";
                  const isHeadlight = booking.type === "headlight_restoration";
                  const refundDeadline = new Date(booking.refund_deadline);
                  const timeLeft = Math.max(0, Math.floor((refundDeadline - new Date()) / (1000 * 60 * 60)));
                  
                  return (
                    <div key={booking.id} className="border p-5" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }} data-testid={`paid-booking-${booking.id}`}>
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        {/* Icon & Type */}
                        <div className={`w-12 h-12 flex items-center justify-center flex-shrink-0 ${
                          isInspection ? "bg-[#3b82f6]/10 border border-[#3b82f6]/30" : "bg-yellow-500/10 border border-yellow-500/30"
                        }`}>
                          {isInspection ? (
                            <Truck size={20} className="text-[#3b82f6]" />
                          ) : (
                            <span className="text-yellow-400 text-lg">💡</span>
                          )}
                        </div>

                        {/* Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h4 className="font-heading text-base uppercase tracking-wide" style={{ color: 'var(--dash-text)' }}>
                              {booking.name}
                            </h4>
                            <span data-testid={`booking-status-${booking.id}`} className={`px-2 py-0.5 text-xs border ${
                              booking.status === "paid" ? "bg-[#22c55e]/20 text-[#22c55e] border-[#22c55e]/30" :
                              booking.status === "refunded" ? "bg-red-500/20 text-red-400 border-red-500/30" :
                              "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                            }`}>
                              {booking.status.toUpperCase()}
                            </span>
                            <span data-testid={`booking-type-${booking.id}`} className="px-2 py-0.5 text-xs" style={{ backgroundColor: 'var(--dash-border)', color: 'var(--dash-text-muted)', border: '1px solid var(--dash-border)' }}>
                              {isInspection ? "Mobile Inspection" : "Headlight Restoration"}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm font-body" style={{ color: 'var(--dash-text-muted)' }}>
                            <div className="flex items-center gap-2">
                              <Car size={14} style={{ color: 'var(--dash-text-dim)' }} />
                              <span>{booking.vehicle_year} {booking.vehicle_make} {booking.vehicle_model}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone size={14} style={{ color: 'var(--dash-text-dim)' }} />
                              <span>{booking.phone}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Mail size={14} style={{ color: 'var(--dash-text-dim)' }} />
                              <span>{booking.email}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CalendarDays size={14} style={{ color: 'var(--dash-text-dim)' }} />
                              <span>{booking.preferred_date} at {booking.preferred_time}</span>
                            </div>
                            {isInspection && booking.address && (
                              <div className="flex items-center gap-2 md:col-span-2">
                                <span style={{ color: 'var(--dash-text-dim)' }}>📍</span>
                                <span>{booking.address}, {booking.suburb}</span>
                              </div>
                            )}
                            {isHeadlight && booking.service_type && (
                              <div className="flex items-center gap-2">
                                <span style={{ color: 'var(--dash-text-dim)' }}>🔧</span>
                                <span className="capitalize">{booking.service_type}</span>
                              </div>
                            )}
                          </div>

                          {booking.notes && (
                            <p className="text-sm mt-2 italic font-body" style={{ color: 'var(--dash-text-muted)' }}>"{booking.notes}"</p>
                          )}
                        </div>

                        {/* Amount & Actions */}
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <div className="text-right" data-testid={`booking-amount-${booking.id}`}>
                            <p className="font-heading text-2xl text-[#22c55e]">
                              ${isInspection ? "150" : (booking.deposit_amount || 50)}<span className="text-sm" style={{ color: 'var(--dash-text-muted)' }}>.00</span>
                            </p>
                            {isHeadlight && (
                              <p className="text-xs" style={{ color: 'var(--dash-text-muted)' }}>
                                Remaining: ${booking.remaining_amount || 200}
                              </p>
                            )}
                          </div>

                          {booking.status === "paid" && (
                            <div className="text-right">
                              {isRefundable ? (
                                <>
                                  <p className="text-xs mb-1" style={{ color: 'var(--dash-text-dim)' }} data-testid={`refund-time-left-${booking.id}`}>{timeLeft}h left to refund</p>
                                  <button
                                    onClick={() => handleRefundBooking(booking.id)}
                                    disabled={refundingBooking === booking.id}
                                    data-testid={`refund-btn-${booking.id}`}
                                    className="px-3 py-1.5 text-xs uppercase tracking-wider bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors cursor-pointer disabled:opacity-50"
                                  >
                                    {refundingBooking === booking.id ? (
                                      <Loader2 size={12} className="animate-spin inline mr-1" />
                                    ) : null}
                                    Refund
                                  </button>
                                </>
                              ) : (
                                <span className="text-xs" style={{ color: 'var(--dash-text-dim)' }} data-testid={`refund-expired-${booking.id}`}>Refund period expired</span>
                              )}
                            </div>
                          )}

                          {booking.status === "refunded" && (
                            <span className="text-xs text-red-400">
                              Refunded {booking.refunded_at ? new Date(booking.refunded_at).toLocaleDateString("en-AU") : ""}
                            </span>
                          )}

                          <span className="text-xs font-mono" style={{ color: 'var(--dash-text-dim)' }}>
                            {new Date(booking.created_at).toLocaleString("en-AU")}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Labor Report Tab */}
        {activeTab === "labor" && (
          <div className="space-y-4" data-testid="labor-tab">
            {/* Header with week navigation */}
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading text-lg uppercase tracking-wide" style={{ color: 'var(--dash-text)' }}>Weekly Labor Report</h3>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => {
                      if (!laborReport?.users?.length) { toast.info("No data to export"); return; }
                      const rows = [["User","Date","Vehicle","Customer","Clock In","Clock Out","Duration (min)","Notes"]];
                      laborReport.users.forEach(u => {
                        u.entries.forEach(e => {
                          rows.push([
                            u.display_name,
                            new Date(e.clock_in).toLocaleDateString("en-AU"),
                            e.vehicle,
                            e.customer || "",
                            new Date(e.clock_in).toLocaleTimeString("en-AU",{hour:"2-digit",minute:"2-digit"}),
                            new Date(e.clock_out).toLocaleTimeString("en-AU",{hour:"2-digit",minute:"2-digit"}),
                            e.duration_minutes.toFixed(2),
                            (e.notes || "").replace(/"/g, '""'),
                          ]);
                        });
                      });
                      rows.push([]);
                      rows.push(["TOTAL HOURS", laborReport.grand_total_hours]);
                      const csv = rows.map(r => r.map(c => `"${c}"`).join(",")).join("\n");
                      const blob = new Blob([csv], { type: "text/csv" });
                      const a = document.createElement("a");
                      a.href = URL.createObjectURL(blob);
                      a.download = `labor-report-${laborReport.week_label?.replace(/ /g,"-") || "export"}.csv`;
                      a.click();
                      toast.success("CSV downloaded!");
                    }}
                    className="px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors cursor-pointer"
                    style={{ backgroundColor: 'var(--dash-border)', borderColor: 'var(--dash-border)', color: 'var(--dash-text-muted)' }}
                    data-testid="labor-export-csv"
                  >
                    <FileText size={12} className="inline mr-1.5" />CSV
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await api.post(`${API}/admin/send-labor-report?report_type=weekly`);
                        toast.success("Weekly labor report emailed!");
                      } catch { toast.error("Failed to send report"); }
                    }}
                    className="px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors cursor-pointer"
                    style={{ backgroundColor: 'var(--dash-border)', borderColor: 'var(--dash-border)', color: 'var(--dash-text-muted)' }}
                    data-testid="labor-send-weekly"
                  >
                    <Mail size={12} className="inline mr-1.5" />Weekly
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const res = await api.post(`${API}/admin/send-labor-report?report_type=monthly`);
                        if (res.data.sent) toast.success("Monthly labor report emailed!");
                        else toast.info("No data for last month");
                      } catch { toast.error("Failed to send report"); }
                    }}
                    className="px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors cursor-pointer"
                    style={{ backgroundColor: 'var(--dash-border)', borderColor: 'var(--dash-border)', color: 'var(--dash-text-muted)' }}
                    data-testid="labor-send-monthly"
                  >
                    <Mail size={12} className="inline mr-1.5" />Monthly
                  </button>
                </div>
                <div className="w-px h-5" style={{ backgroundColor: 'var(--dash-border)' }}></div>
                <div className="flex items-center gap-2">
                <button
                  onClick={() => setLaborWeekOffset(prev => prev - 1)}
                  className="px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors cursor-pointer"
                  style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)', color: 'var(--dash-text-muted)' }}
                  data-testid="labor-prev-week"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="text-sm font-mono min-w-[160px] text-center" style={{ color: 'var(--dash-text-muted)' }} data-testid="labor-week-label">
                  {laborReport?.week_label || "Loading..."}
                </span>
                <button
                  onClick={() => setLaborWeekOffset(prev => Math.min(prev + 1, 0))}
                  disabled={laborWeekOffset >= 0}
                  className="px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)', color: 'var(--dash-text-muted)' }}
                  data-testid="labor-next-week"
                >
                  <ChevronRight size={14} />
                </button>
                {laborWeekOffset !== 0 && (
                  <button
                    onClick={() => setLaborWeekOffset(0)}
                    className="px-3 py-1.5 text-xs uppercase tracking-wider bg-[#3b82f6]/10 border border-[#3b82f6]/30 text-[#3b82f6] hover:bg-[#3b82f6]/20 transition-colors cursor-pointer"
                  >
                    This Week
                  </button>
                )}
                </div>
              </div>
            </div>

            {laborLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 size={24} className="animate-spin" style={{ color: 'var(--dash-text-dim)' }} />
              </div>
            ) : laborReport ? (
              <>
                {/* Grand Totals */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="border p-5 text-center" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
                    <p className="font-heading text-3xl" style={{ color: 'var(--dash-text)' }} data-testid="labor-total-hours">{laborReport.grand_total_hours}h</p>
                    <p className="text-xs uppercase tracking-wider font-body mt-1" style={{ color: 'var(--dash-text-muted)' }}>Total Hours</p>
                  </div>
                  <div className="border p-5 text-center" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
                    <p className="font-heading text-3xl text-[#3b82f6]">{laborReport.users?.length || 0}</p>
                    <p className="text-xs uppercase tracking-wider font-body mt-1" style={{ color: 'var(--dash-text-muted)' }}>Team Members</p>
                  </div>
                  <div className="border p-5 text-center" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
                    <p className="font-heading text-3xl" style={{ color: 'var(--dash-text-muted)' }}>{laborReport.total_entries}</p>
                    <p className="text-xs uppercase tracking-wider font-body mt-1" style={{ color: 'var(--dash-text-muted)' }}>Clock Entries</p>
                  </div>
                </div>

                {/* Per-User Breakdown */}
                {laborReport.users?.length === 0 ? (
                  <div className="border p-12 text-center" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
                    <Clock size={32} className="mx-auto mb-4" style={{ color: 'var(--dash-text-dim)' }} />
                    <p className="font-body" style={{ color: 'var(--dash-text-muted)' }}>No time tracked this week</p>
                    <p className="text-sm font-body mt-2" style={{ color: 'var(--dash-text-dim)' }}>Clock in on a job to start tracking</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {laborReport.users.map((user) => (
                      <div key={user.user} className="border" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }} data-testid={`labor-user-${user.user}`}>
                        {/* User Header */}
                        <div
                          className="p-5 flex items-center justify-between cursor-pointer"
                          onClick={() => setExpandedLaborUser(expandedLaborUser === user.user ? null : user.user)}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-[#3b82f6]/10 border border-[#3b82f6]/20 flex items-center justify-center">
                              <span className="font-heading text-sm text-[#3b82f6]">{user.user.replace('VPP','').charAt(0)}</span>
                            </div>
                            <div>
                              <h4 className="font-heading text-base uppercase tracking-wide" style={{ color: 'var(--dash-text)' }}>{user.user.replace('VPP','')}</h4>
                              <p className="font-body text-sm" style={{ color: 'var(--dash-text-muted)' }}>{user.jobs.length} job{user.jobs.length !== 1 ? 's' : ''} worked on</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-heading text-xl" style={{ color: 'var(--dash-text)' }}>{user.total_hours}h</p>
                              <p className="text-xs font-mono" style={{ color: 'var(--dash-text-dim)' }}>{user.total_minutes} mins</p>
                            </div>
                            {expandedLaborUser === user.user ? <ChevronUp size={16} style={{ color: 'var(--dash-text-dim)' }} /> : <ChevronDown size={16} style={{ color: 'var(--dash-text-dim)' }} />}
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {expandedLaborUser === user.user && (
                          <div className="border-t p-5 space-y-4" style={{ borderColor: 'var(--dash-border)' }}>
                            {/* Jobs summary */}
                            <div>
                              <p className="text-xs uppercase tracking-wider font-body mb-2" style={{ color: 'var(--dash-text-muted)' }}>Jobs</p>
                              <div className="space-y-2">
                                {user.jobs.map((job) => (
                                  <div key={job.job_id} className="flex items-center justify-between p-3 border" style={{ backgroundColor: 'var(--dash-bg)', borderColor: 'var(--dash-border)' }}>
                                    <div>
                                      <p className="text-sm font-body" style={{ color: 'var(--dash-text)' }}>{job.vehicle}</p>
                                      <p className="text-xs" style={{ color: 'var(--dash-text-dim)' }}>{job.customer}</p>
                                    </div>
                                    <span className="font-mono text-sm text-[#3b82f6]">{job.minutes < 60 ? `${Math.round(job.minutes)}m` : `${(job.minutes / 60).toFixed(1)}h`}</span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Time entries with notes */}
                            <div>
                              <p className="text-xs uppercase tracking-wider font-body mb-2" style={{ color: 'var(--dash-text-muted)' }}>Time Entries</p>
                              <div className="space-y-1">
                                {user.entries.map((entry, i) => (
                                  <div key={i} className="flex items-start gap-3 py-2 border-b last:border-none" style={{ borderColor: 'var(--dash-border)' }}>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-body" style={{ color: 'var(--dash-text-muted)' }}>{entry.vehicle}</span>
                                        <span style={{ color: 'var(--dash-text-dim)' }}>·</span>
                                        <span className="text-xs font-mono" style={{ color: 'var(--dash-text-muted)' }}>
                                          {new Date(entry.clock_in).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}
                                          {" → "}
                                          {new Date(entry.clock_out).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}
                                        </span>
                                        <span className="text-xs font-mono text-[#3b82f6]">{entry.duration_minutes < 60 ? `${Math.round(entry.duration_minutes)}m` : `${(entry.duration_minutes / 60).toFixed(1)}h`}</span>
                                      </div>
                                      {entry.notes && (
                                        <p className="text-xs mt-0.5 italic font-body" style={{ color: 'var(--dash-text-muted)' }}>"{entry.notes}"</p>
                                      )}
                                    </div>
                                    <span className="text-[10px] font-mono whitespace-nowrap" style={{ color: 'var(--dash-text-dim)' }}>
                                      {new Date(entry.clock_in).toLocaleDateString("en-AU", { weekday: "short" })}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}

        {/* Add Entry Modal */}
        {showAddModal && (
          <AddEntryModal 
            type={showAddModal} 
            onClose={() => setShowAddModal(null)} 
            onSaved={showAddModal === "quote" ? refreshQuotes : refreshAppointments} 
          />
        )}

        {/* Awaiting Car Modal - Set Drop-off Date */}
        {awaitingCarModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4" onClick={() => setAwaitingCarModal(null)} data-testid="awaiting-car-modal">
            <div className="border w-full max-w-md p-6" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }} onClick={e => e.stopPropagation()}>
              <h3 className="font-heading text-lg uppercase tracking-wider mb-1" style={{ color: 'var(--dash-text)' }}>Set Drop-off Date</h3>
              <p className="text-sm font-body mb-4" style={{ color: 'var(--dash-text-muted)' }}>
                When will {awaitingCarModal.name} drop off their {awaitingCarModal.vehicle_model}?
              </p>
              <input
                type="date"
                value={expectedDeliveryDate}
                onChange={e => setExpectedDeliveryDate(e.target.value)}
                className="w-full border text-sm p-3 mb-4 focus:outline-none focus:border-[#3b82f6]/50 font-body"
                style={{ backgroundColor: 'var(--dash-border)', borderColor: 'var(--dash-border)', color: 'var(--dash-text)' }}
                data-testid="expected-delivery-date-input"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => { setAwaitingCarModal(null); setExpectedDeliveryDate(""); }}
                  className="flex-1 px-4 py-2 text-xs uppercase tracking-wider border transition-colors cursor-pointer bg-transparent"
                  style={{ borderColor: 'var(--dash-border)', color: 'var(--dash-text-muted)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={setAppointmentAwaitingCar}
                  className="flex-1 px-4 py-2 text-xs uppercase tracking-wider bg-[#3b82f6] text-white border border-[#3b82f6] hover:bg-[#3b82f6]/80 transition-colors cursor-pointer"
                  data-testid="confirm-awaiting-car-btn"
                >
                  Confirm & Send Email
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quote Awaiting Car Modal - Set Drop-off Date */}
        {quoteAwaitingCarModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4" onClick={() => setQuoteAwaitingCarModal(null)} data-testid="quote-awaiting-car-modal">
            <div className="border w-full max-w-md p-6" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }} onClick={e => e.stopPropagation()}>
              <h3 className="font-heading text-lg uppercase tracking-wider mb-1" style={{ color: 'var(--dash-text)' }}>
                {quoteAwaitingCarModal.expected_delivery_date ? 'Update Drop-off Date' : 'Set Drop-off Date'}
              </h3>
              <p className="text-sm font-body mb-4" style={{ color: 'var(--dash-text-muted)' }}>
                When will {quoteAwaitingCarModal.name} drop off their {quoteAwaitingCarModal.vehicle_model}?
              </p>
              {quoteAwaitingCarModal.expected_delivery_date && (
                <p className="text-xs font-body mb-2" style={{ color: 'var(--dash-text-dim)' }}>
                  Current date: <span className="text-[#f59e0b]">{quoteAwaitingCarModal.expected_delivery_date}</span>
                </p>
              )}
              <input
                type="date"
                value={expectedDeliveryDate || quoteAwaitingCarModal.expected_delivery_date || ''}
                onChange={e => setExpectedDeliveryDate(e.target.value)}
                className="w-full border text-sm p-3 mb-4 focus:outline-none focus:border-[#3b82f6]/50 font-body"
                style={{ backgroundColor: 'var(--dash-border)', borderColor: 'var(--dash-border)', color: 'var(--dash-text)' }}
                data-testid="quote-expected-delivery-date-input"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => { setQuoteAwaitingCarModal(null); setExpectedDeliveryDate(""); }}
                  className="flex-1 px-4 py-2 text-xs uppercase tracking-wider border transition-colors cursor-pointer bg-transparent"
                  style={{ borderColor: 'var(--dash-border)', color: 'var(--dash-text-muted)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={setQuoteAwaitingCar}
                  className="flex-1 px-4 py-2 text-xs uppercase tracking-wider bg-[#3b82f6] text-white border border-[#3b82f6] hover:bg-[#3b82f6]/80 transition-colors cursor-pointer"
                  data-testid="confirm-quote-awaiting-car-btn"
                >
                  {quoteAwaitingCarModal.expected_delivery_date ? 'Update & Notify Customer' : 'Confirm & Send Email'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Photo Lightbox */}
        {lightboxPhotos && (
          <PhotoLightbox
            photos={lightboxPhotos}
            initialIndex={lightboxIndex}
            onClose={() => setLightboxPhotos(null)}
          />
        )}
      </div>

      {/* Brain-dump floating button (always available) */}
      <BrainDumpButton onCreate={createTask} />

      {/* End-of-day check-in */}
      {showEndOfDay && (
        <EndOfDayModal
          recap={dailyRecap}
          onClose={() => setShowEndOfDay(false)}
          onSave={saveCheckin}
        />
      )}
    </div>
  );
}
